import { useMemo, useState } from 'react';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';
import { Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Badge } from '@Components/Badge';
import { MultiSelectCombobox } from '@Components/MultiSelectCombobox';
import { Skeleton } from '@Components/Skeleton';
import { useAssets } from '@Hooks/useAssets';
import { useAssetTags } from '@Hooks/useAssetTags';
import { useQueryStateFixed } from '@Utils/nuqs-fix';
import { parseAsStringArray } from '@Utils/nuqs-parsers';
import { AssetType, type Asset } from '@Types/assets';

import AssetPreview from './AssetPreview';
import AssetSheet from './AssetSheet';
import UploadAssetsDialog from './UploadAssetsDialog';

export default function AssetGallery() {
  const queryClient = useQueryClient();

  const [selectedTagIds, setSelectedTagIds] = useQueryStateFixed<string[]>(
    'tags',
    parseAsStringArray,
  );

  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const activeTagIds = selectedTagIds ?? [];

  const searchParams = useMemo(
    () => ({
      include: 'tags.tag',
      sort: '-createdAt',
    }),
    [],
  );

  const { data: assets, status: assetsStatus } = useAssets({ searchParams });

  const { data: assetTagsResponse, status: assetTagsStatus } = useAssetTags();
  const assetTags = assetTagsResponse?.data ?? [];

  const tagItems = useMemo(
    () => assetTags.map((tag) => ({ value: tag.id, label: tag.name })),
    [assetTags],
  );

  const filteredAssets = useMemo(() => {
    if (!assets) return [];
    if (activeTagIds.length === 0) return assets;

    return assets.filter((asset) => {
      const assetTagIds = asset.tags?.map((t) => t.tag?.id) ?? [];
      return activeTagIds.some((id) => assetTagIds.includes(id));
    });
  }, [assets, activeTagIds]);

  const handleAssetClick = (asset: Asset) => {
    setSelectedAsset(asset);
    setIsSheetOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="w-64">
          <Label className="mb-1 block text-sm font-medium">
            Filter by tag
          </Label>
          <MultiSelectCombobox
            items={tagItems}
            selectedValues={activeTagIds}
            onChange={(ids) => setSelectedTagIds(ids.length > 0 ? ids : null)}
            placeholder="All tags"
            loading={assetTagsStatus === 'pending'}
          />
        </div>

        <Button
          type="button"
          variant="primary"
          onClick={() => setIsUploadDialogOpen(true)}
        >
          <Plus className="mr-1 h-4 w-4" />
          Upload
        </Button>
      </div>

      {/* Gallery grid */}
      {assetsStatus === 'pending' ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square w-full rounded-md" />
          ))}
        </div>
      ) : filteredAssets.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center text-sm text-zinc-500">
          <div className="font-medium text-zinc-700">No assets found</div>
          <p className="mt-1">
            Upload images or PDFs, or adjust your filters to see results.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {filteredAssets.map((asset) => (
            <button
              key={asset.id}
              type="button"
              onClick={() => handleAssetClick(asset)}
              className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border bg-zinc-200 transition hover:border-zinc-400 hover:shadow-sm"
            >
              <AssetPreview
                asset={asset}
                className="max-h-full max-w-full object-contain"
              />
              {asset.type === AssetType.DOCUMENT && (
                <span className="absolute top-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  PDF
                </span>
              )}
              {asset.tags && asset.tags.length > 0 && (
                <div className="absolute right-0 bottom-0 left-0 flex flex-wrap gap-1 bg-black/50 p-1.5">
                  {asset.tags.map((t) => (
                    <Badge
                      key={t.tag?.id}
                      variant="secondary"
                      className="text-[10px] leading-tight"
                    >
                      {t.tag?.name ?? t.tag?.id}
                    </Badge>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Asset edit sheet */}
      <AssetSheet
        asset={selectedAsset}
        isOpen={isSheetOpen}
        setIsOpen={setIsSheetOpen}
      />

      {/* Upload dialog */}
      <UploadAssetsDialog
        isOpen={isUploadDialogOpen}
        onOpenChange={setIsUploadDialogOpen}
        onUploadSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['assets'] });
        }}
      />
    </div>
  );
}
